/**
 * =============================================================================
 * STANDALONE WATCH CARD COMPONENT - AI-NATIVE DESIGN SYSTEM
 * =============================================================================
 *
 * Featured/highlighted alert card for "AI Insights" section.
 *
 * DESIGN SYSTEM:
 * - Card title: 16-18px
 * - Padding: 20-24px
 * - Semantic left indicator
 * - Minimal badges - no decorative elements
 * - Subtle featured styling (no heavy gradients)
 */

import {
    Box,
    Stack,
    Text,
    Group,
    Skeleton,
    ActionIcon,
    Tooltip,
} from '@mantine/core';
import {
    PencilSimple,
    Warning,
    CaretRight,
    Sparkle,
} from '@phosphor-icons/react';
import { memo, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import type {
    StandaloneWatchCardProps,
    WatchCardState,
    EditDialogConfig,
} from '../../types';
import { evaluateWatchCardAlert } from '../../api';
import { EditDialog } from '../../shared/EditDialog';

// =============================================================================
// DESIGN TOKENS
// =============================================================================
const spacing = {
    xs: 8,
    sm: 16,
    md: 20,
    lg: 24,
} as const;

// Semantic colors
const semanticColors = {
    critical: 'rgb(205, 101, 101)',
    warning: 'rgb(219, 182, 88)',
    positive: 'rgb(45, 187, 136)',
    info: 'rgb(1, 149, 255)',
} as const;

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const StandaloneWatchCard: React.FC<StandaloneWatchCardProps> = memo(
    ({
        tagline,
        config,
        isFeatured = false,
        data: controlledData,
        isLoading: controlledLoading,
        error: controlledError,
        className = '',
        onClick,
        onEditClick,
    }) => {
        const navigate = useNavigate();

        // Local state for uncontrolled mode
        const [state, setState] = useState<WatchCardState>({
            data: null,
            isLoading: true,
            error: null,
            lastFetched: null,
        });

        // Edit dialog state
        const [editDialogOpen, setEditDialogOpen] = useState(false);
        const [isSaving, setIsSaving] = useState(false);

        // Determine if controlled or uncontrolled
        const isControlled = controlledData !== undefined;

        // Use controlled values if provided, otherwise use local state
        const displayData = isControlled ? controlledData : state.data;
        const displayLoading =
            controlledLoading ?? (!isControlled && state.isLoading);
        const displayError = controlledError ?? state.error;

        // Fetch alert data on mount (uncontrolled mode only)
        useEffect(() => {
            if (!isControlled) {
                const loadData = async () => {
                    setState((prev) => ({
                        ...prev,
                        isLoading: true,
                        error: null,
                    }));
                    const result = await evaluateWatchCardAlert(config);
                    setState(result);
                };

                loadData();
            }
        }, [config, isControlled]);

        // Handle card click - navigate to alert page (only if we have data)
        const handleClick = useCallback(() => {
            if (onClick) {
                onClick();
            } else if (displayData?.checklistItemId) {
                // Only navigate if we have a checklistItemId
                const path = config.onClickPath.replace(
                    ':alertId',
                    displayData.checklistItemId,
                );
                navigate(path);
            }
            // If no data, clicking on card does nothing
        }, [onClick, navigate, config.onClickPath, displayData]);

        // Handle edit click (stop propagation to prevent card click)
        const handleEditClick = useCallback(
            (e: React.MouseEvent) => {
                e.stopPropagation();
                if (onEditClick) {
                    onEditClick();
                } else {
                    setEditDialogOpen(true);
                }
            },
            [onEditClick],
        );

        // Handle edit save
        const handleEditSave = useCallback(async (data: unknown) => {
            setIsSaving(true);
            console.log('[StandaloneWatchCard] Saving edits:', data);
            await new Promise((resolve) => setTimeout(resolve, 500));
            setIsSaving(false);
            setEditDialogOpen(false);
        }, []);

        // Build edit dialog config
        const editDialogConfig: EditDialogConfig | null = useMemo(() => {
            if (!config.isEditable || !config.editConfig) return null;

            return {
                entityId: config.id,
                entityType: 'standalone-watch-card',
                entityLabel:
                    displayData?.heading ||
                    config.fallback?.heading ||
                    'Featured Alert',
                reference: {
                    ...config.editConfig.reference,
                    title:
                        config.editConfig.reference?.title ||
                        displayData?.heading ||
                        config.fallback?.heading ||
                        'Alert',
                    description:
                        config.editConfig.reference?.description ||
                        displayData?.summary ||
                        config.fallback?.summary,
                },
                agentConfig: config.editConfig.agentConfig,
            };
        }, [config, displayData]);

        return (
            <>
                <Stack
                    className={`h-full ${className}`}
                    style={{ gap: spacing.sm }}
                >
                    {/* Section header with AI indicator */}
                    {tagline && (
                        <Group style={{ gap: spacing.xs }}>
                            <Sparkle
                                size={14}
                                weight="fill"
                                className="text-slate-400"
                            />
                            <Text
                                className="text-slate-900"
                                style={{
                                    fontSize: 18,
                                    fontWeight: 600,
                                    lineHeight: 1.3,
                                }}
                            >
                                {tagline}
                            </Text>
                        </Group>
                    )}

                    {/* Card */}
                    <Box
                        className="group relative flex-1 cursor-pointer"
                        style={{
                            backgroundColor: 'white',
                            borderRadius: 12,
                            padding: spacing.lg,
                            paddingLeft: spacing.lg + 8,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                            transition: 'all 0.2s ease',
                            overflow: 'hidden',
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                        onClick={handleClick}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.boxShadow =
                                '0 4px 12px rgba(0,0,0,0.08)';
                            e.currentTarget.style.transform =
                                'translateY(-1px)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.boxShadow =
                                '0 1px 3px rgba(0,0,0,0.04)';
                            e.currentTarget.style.transform = 'translateY(0)';
                        }}
                    >
                        {/* Semantic indicator - info blue for AI insights */}
                        <Box
                            style={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: 4,
                                backgroundColor: semanticColors.info,
                                borderRadius: '12px 0 0 12px',
                            }}
                        />

                        {/* Header row: Category + Edit */}
                        <Group
                            className="items-start justify-between"
                            style={{ marginBottom: spacing.xs }}
                        >
                            {config.categoryLabel && (
                                <Text
                                    className="tracking-wider text-slate-400 uppercase"
                                    style={{
                                        fontSize: 10,
                                        fontWeight: 600,
                                        letterSpacing: '0.05em',
                                    }}
                                >
                                    {config.categoryLabel}
                                </Text>
                            )}

                            {/* Edit button */}
                            {config.isEditable && (
                                <Tooltip label="Edit alert" position="top">
                                    <ActionIcon
                                        variant="subtle"
                                        color="gray"
                                        size="xs"
                                        onClick={handleEditClick}
                                        className="opacity-0 transition-opacity group-hover:opacity-100"
                                    >
                                        <PencilSimple size={14} weight="bold" />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                        </Group>

                        {/* Content */}
                        {displayLoading ? (
                            <Stack style={{ gap: spacing.xs }}>
                                <Skeleton height={20} width="70%" radius={4} />
                                <Skeleton height={14} width="90%" radius={4} />
                                <Skeleton height={14} width="60%" radius={4} />
                            </Stack>
                        ) : displayError && !displayData ? (
                            <Group
                                style={{
                                    gap: spacing.xs,
                                    color: semanticColors.critical,
                                }}
                                className="justify-center py-4"
                            >
                                <Warning size={20} weight="fill" />
                                <Text style={{ fontSize: 13 }}>
                                    Failed to load
                                </Text>
                            </Group>
                        ) : !displayData ? (
                            // No data available - show "Activate Story" button
                            <Box
                                style={{
                                    flex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: 80,
                                }}
                            >
                                <Box
                                    style={{
                                        backgroundColor:
                                            'rgba(99, 102, 241, 0.08)',
                                        color: 'rgb(99, 102, 241)',
                                        fontWeight: 600,
                                        fontSize: 14,
                                        padding: '10px 20px',
                                        borderRadius: 8,
                                        cursor: 'default',
                                    }}
                                >
                                    Activate Story
                                </Box>
                            </Box>
                        ) : (
                            <Stack
                                style={{ gap: spacing.xs }}
                                className="flex-1"
                            >
                                {/* Heading - 16-18px */}
                                <Text
                                    className="text-slate-900"
                                    style={{
                                        fontSize: 16,
                                        fontWeight: 600,
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {displayData.heading}
                                </Text>

                                {/* Summary - muted */}
                                {displayData.summary && (
                                    <Text
                                        className="flex-1 text-slate-400"
                                        style={{
                                            fontSize: 13,
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {displayData.summary}
                                    </Text>
                                )}
                            </Stack>
                        )}

                        {/* Action hint - only show when we have data */}
                        {displayData && (
                            <Group
                                style={{
                                    gap: 4,
                                    marginTop: spacing.sm,
                                    paddingTop: spacing.xs,
                                    borderTop: '1px solid rgba(0,0,0,0.04)',
                                }}
                            >
                                <Text
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 500,
                                        color: semanticColors.info,
                                    }}
                                >
                                    Take action
                                </Text>
                                <CaretRight
                                    size={12}
                                    weight="bold"
                                    style={{ color: semanticColors.info }}
                                />
                            </Group>
                        )}
                    </Box>
                </Stack>

                {/* Edit Dialog */}
                {config.isEditable && editDialogConfig && (
                    <EditDialog
                        opened={editDialogOpen}
                        config={editDialogConfig}
                        onClose={() => setEditDialogOpen(false)}
                        onSave={handleEditSave}
                        isSaving={isSaving}
                    />
                )}
            </>
        );
    },
);

StandaloneWatchCard.displayName = 'StandaloneWatchCard';

export default StandaloneWatchCard;
